import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";

import { Checkbox, Dropdown, ErrorState, FormField, Icon, StateMessage } from "@/ds";
import { CampoDecimal } from "@/shared/components";
import {
  CARREGANDO_LABEL,
  CLASSIFICACAO_PLACEHOLDER,
  COTACAO_OBRA_TEXTO,
  COTACAO_OBRA_TITLE,
  DEFINIR_COTACAO_LABEL,
  EDITAR_COTACAO_LABEL,
  FATOR_APLICAR_LABEL,
  FATOR_CAMPO_LABEL,
  MOEDA_LABEL,
  PENDENTE_COTACAO,
  PROJECAO_SECAO,
  SELECIONE_PLACEHOLDER,
  TOTAL_ESCOPO_LABEL,
} from "./projecao-formulario.constants";
import { ProjecaoColetoresTabela } from "./projecao-coletores-tabela";
import modal from "../planejamento-controle.modal.module.scss";
import { COTACAO_PENDENTE_TITULO } from "../planejamento-controle.constants";
import styles from "./projecao-formulario.module.scss";
import type { ProjecaoFormularioViewProps } from "./types-projecao-formulario";

const PAINEL = { panel: { className: modal.painel } };
const ROTULO = { label: modal.rotuloCampo };

type Bloco = ProjecaoFormularioViewProps["servicos"];

function Categoria({ bloco, desabilitado }: { bloco: Bloco; desabilitado: boolean }) {
  return (
    <section className={styles.cartao} data-testid={bloco.ids.cartao}>
      <h3 className={styles.cartaoTitulo}>{bloco.titulo}</h3>
      <div className={styles.valorMoeda}>
        <CampoDecimal
          label={bloco.valorLabel}
          prefixo={bloco.prefixo}
          casas={2}
          value={bloco.valor}
          onChange={bloco.digitarValor}
          error={bloco.erros.valor ?? null}
          disabled={desabilitado || bloco.travado}
          testId={bloco.ids.valor}
        />
        <FormField.Root disabled={desabilitado || bloco.travado}>
          <FormField.Label>{MOEDA_LABEL}</FormField.Label>
          <FormField.Control>
            <Dropdown
              appendTo="self"
              pt={PAINEL}
              value={bloco.moedaId}
              options={bloco.moedas}
              onChange={(evento) => bloco.escolherMoeda(evento.value)}
              data-testid={bloco.ids.moeda}
            />
          </FormField.Control>
        </FormField.Root>
      </div>
      {bloco.pendenteCotacao ? (
        <p className={styles.pendente} data-testid={bloco.ids.pendente}>
          {PENDENTE_COTACAO}
        </p>
      ) : null}
      {bloco.conversao ? (
        <p className={styles.conversao} data-testid={bloco.ids.conversao}>
          {bloco.conversao}
        </p>
      ) : null}
      <FormField.Root
        classNames={ROTULO}
        error={bloco.erros.distribuicaoId}
        disabled={desabilitado}
      >
        <FormField.Label>{bloco.distribuicaoLabel}</FormField.Label>
        <FormField.Control>
          <Dropdown
            appendTo="self"
            pt={PAINEL}
            value={bloco.distribuicaoId}
            options={bloco.distribuicoes}
            placeholder={SELECIONE_PLACEHOLDER}
            onChange={(evento) => bloco.escolherDistribuicao(evento.value)}
            data-testid={bloco.ids.distribuicao}
          />
        </FormField.Control>
        <FormField.Error data-testid={bloco.ids.distribuicaoErro} />
      </FormField.Root>
      <FormField.Root classNames={ROTULO} error={bloco.erros.curvaId} disabled={desabilitado}>
        <FormField.Label>{bloco.curvaLabel}</FormField.Label>
        <FormField.Control>
          <Dropdown
            appendTo="self"
            pt={PAINEL}
            value={bloco.curvaId}
            options={bloco.curvas}
            placeholder={SELECIONE_PLACEHOLDER}
            onChange={(evento) => bloco.escolherCurva(evento.value)}
            data-testid={bloco.ids.curva}
          />
        </FormField.Control>
        <FormField.Error data-testid={bloco.ids.curvaErro} />
      </FormField.Root>
    </section>
  );
}

export function ProjecaoFormularioView({
  estado,
  errorMessage,
  recarregar,
  enviando,
  cotacaoDaObra,
  servicos,
  materiais,
  custoPadrao,
  semCustoPadrao,
  fator,
  classificacao,
  totalEscopo,
  tabela,
  moedaPendente,
  definirCotacaoPendente,
  editarCotacao,
}: ProjecaoFormularioViewProps) {
  const carregando = estado === "loading";
  const bloqueado = enviando || estado !== "success";

  return (
    <div className={styles.corpo}>
      {classificacao ? (
        <section className={styles.faixa} data-testid="projecao-classificacao">
          <h3 className={styles.faixaTitulo}>{classificacao.titulo}</h3>
          <FormField.Root disabled={bloqueado}>
            <FormField.Control>
              <Dropdown
                appendTo="self"
                pt={PAINEL}
                value={classificacao.valor}
                options={classificacao.opcoes}
                placeholder={CLASSIFICACAO_PLACEHOLDER}
                onChange={(evento) => classificacao.escolher(evento.value)}
                data-testid="projecao-classificacao-campo"
              />
            </FormField.Control>
          </FormField.Root>
          {classificacao.pendente ? (
            <Tag
              value={classificacao.pendente}
              severity="warning"
              data-testid="projecao-classificacao-pendente"
            />
          ) : null}
        </section>
      ) : null}

      {moedaPendente ? (
        <aside className={styles.cotacaoPendente} data-testid="projecao-cotacao-pendente">
          <Icon name="dollar" size={18} />
          <div className={styles.cotacaoPendenteTextos}>
            <p className={styles.cotacaoTitulo}>{COTACAO_PENDENTE_TITULO(moedaPendente)}</p>
          </div>
          <Button
            type="button"
            label={DEFINIR_COTACAO_LABEL}
            onClick={definirCotacaoPendente}
            data-testid="projecao-definir-cotacao"
          />
        </aside>
      ) : cotacaoDaObra ? (
        <aside className={styles.cotacao} data-testid="projecao-cotacao-obra">
          <Icon name="dollar" size={18} />
          <div>
            <p className={styles.cotacaoTitulo}>{COTACAO_OBRA_TITLE}</p>
            <p className={styles.cotacaoValor}>
              {cotacaoDaObra.moeda} = R$ {cotacaoDaObra.valor}
            </p>
          </div>
          <p className={styles.cotacaoTexto}>{COTACAO_OBRA_TEXTO}</p>
          {editarCotacao ? (
            <Button
              type="button"
              link
              size="small"
              label={EDITAR_COTACAO_LABEL}
              disabled={bloqueado}
              onClick={editarCotacao}
              data-testid="projecao-editar-cotacao"
            />
          ) : null}
        </aside>
      ) : null}

      {semCustoPadrao ? (
        <aside className={styles.faixa} data-testid="projecao-sem-custo-padrao">
          <p className={styles.faixaTitulo}>{semCustoPadrao.titulo}</p>
          <p className={styles.faixaTexto}>{semCustoPadrao.texto}</p>
        </aside>
      ) : null}
      {custoPadrao ? (
        <section className={styles.faixa} data-testid="projecao-custo-padrao">
          <label className={styles.faixaLinha}>
            <Checkbox
              checked={custoPadrao.usando}
              disabled={custoPadrao.desabilitado || bloqueado}
              onChange={(evento) => custoPadrao.alternar(!!evento.checked)}
              data-testid="projecao-custo-padrao-usar"
            />
            <span className={styles.faixaTitulo}>{custoPadrao.titulo}</span>
            {custoPadrao.tag ? (
              <Tag
                value={custoPadrao.tag}
                severity="info"
                data-testid="projecao-custo-padrao-tag"
              />
            ) : null}
          </label>
          <p className={styles.faixaTexto} data-testid="projecao-custo-padrao-valores">
            {custoPadrao.valores}
          </p>
          {custoPadrao.aplicado ? (
            <p className={styles.faixaTexto} data-testid="projecao-custo-padrao-aplicado">
              {custoPadrao.aplicado}
              {custoPadrao.variacao ? ` · ${custoPadrao.variacao}` : ""}
            </p>
          ) : null}
          {custoPadrao.usando ? (
            <Button
              type="button"
              link
              size="small"
              label={custoPadrao.voltarLabel}
              onClick={custoPadrao.voltar}
              data-testid="projecao-custo-padrao-voltar"
            />
          ) : null}
        </section>
      ) : null}
      {fator ? (
        <section className={styles.faixa} data-testid="projecao-fator">
          <div className={styles.faixaLinha}>
            <div className={styles.faixaTextos}>
              <p className={styles.faixaTitulo}>{fator.titulo}</p>
              <p className={styles.faixaTexto}>{fator.texto}</p>
            </div>
            <div className={styles.fatorCampo}>
              <CampoDecimal
                label={FATOR_CAMPO_LABEL}
                labelOculto
                casas={2}
                value={fator.valor}
                onChange={fator.digitar}
                error={fator.erro}
                disabled={bloqueado || !fator.aplicar}
                testId="projecao-fator-valor"
              />
            </div>
            <label className={styles.faixaLinha}>
              <InputSwitch
                checked={fator.aplicar}
                disabled={bloqueado}
                onChange={(evento) => fator.alternarAplicar(!!evento.value)}
                data-testid="projecao-fator-aplicar"
              />
              <span className={styles.faixaTexto}>{FATOR_APLICAR_LABEL}</span>
            </label>
          </div>
        </section>
      ) : null}

      {carregando ? (
        <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="projecao-loading" />
      ) : null}
      {estado === "error" || estado === "forbidden" ? (
        <ErrorState
          message={errorMessage ?? undefined}
          onRetry={recarregar}
          testId="projecao-opcoes-erro"
        />
      ) : null}

      <h2 className={styles.secao}>{PROJECAO_SECAO}</h2>
      <div className={styles.cartoes}>
        <Categoria bloco={servicos} desabilitado={bloqueado} />
        <Categoria bloco={materiais} desabilitado={bloqueado} />
        <section className={styles.cartao} data-testid="projecao-total">
          <h3 className={styles.cartaoTitulo}>{TOTAL_ESCOPO_LABEL}</h3>
          <p className={styles.total} data-testid="projecao-total-valor">
            {totalEscopo}
          </p>
        </section>
      </div>

      <ProjecaoColetoresTabela {...tabela} />
    </div>
  );
}
